import {
  ContextType,
  MutableRefObject,
  RefObject,
  useCallback,
  useEffect
} from 'react';
import { OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';
import { highlightCreator } from '@pages/MediaViewer/AssetViewer/Canvas/Objects/highlightHandler';
import { isMobilePdfPageRendered } from '@pages/MediaViewer/AssetViewer/PdfCard/utils';
import { FabricContext } from '@context/FabricContext';
import { IAssetViewerStateContextValue } from '@context/AssetViewerStateContext';

type UseMobilePdfFabricCanvasParams = {
  fabricContext: ContextType<typeof FabricContext>;
  wrapperRef: RefObject<OverlayScrollbarsComponentRef<'div'>>;
  contentRef: RefObject<HTMLDivElement>;
  canvasContainerRef: RefObject<HTMLDivElement>;
  pageRefsMap: MutableRefObject<Map<number, HTMLDivElement>>;
  activePageNumber: number;
  zoomFactor: number;
  isPageSizeReady: boolean;
  selectedCommentId: string | null;
  pageRefsVersion: number;
  mobilePagesReadyVersion: number;
  selectComment: IAssetViewerStateContextValue['selectComment'];
  setCanvasMode: IAssetViewerStateContextValue['setCanvasMode'];
  setDocumentHighlight: IAssetViewerStateContextValue['setDocumentHighlight'];
};

export function useMobilePdfFabricCanvas({
  fabricContext,
  wrapperRef,
  contentRef,
  canvasContainerRef,
  pageRefsMap,
  activePageNumber,
  zoomFactor,
  isPageSizeReady,
  selectedCommentId,
  pageRefsVersion,
  mobilePagesReadyVersion,
  selectComment,
  setCanvasMode,
  setDocumentHighlight
}: UseMobilePdfFabricCanvasParams) {
  const setMobileFabricCanvasVisible = useCallback(
    (visible: boolean) => {
      const canvasContainer = canvasContainerRef.current;
      if (!canvasContainer) return;
      canvasContainer.style.visibility = visible ? 'visible' : 'hidden';
      canvasContainer.style.pointerEvents = visible ? '' : 'none';
    },
    [canvasContainerRef]
  );

  const syncMobileFabricCanvas = useCallback((): boolean => {
    const canvasContainer = canvasContainerRef?.current;
    const content = contentRef?.current;
    if (!canvasContainer || !content || !fabricContext.canvas) return false;

    const pageEl = pageRefsMap.current.get(activePageNumber);
    if (!pageEl || !isMobilePdfPageRendered(pageEl)) return false;

    const targetEl =
      (pageEl.querySelector('.media_viewer_pdf-inner') as HTMLElement | null) ??
      pageEl;

    const rect = targetEl.getBoundingClientRect();
    if (!rect.width || !rect.height) return false;

    content.style.removeProperty('width');
    content.style.removeProperty('height');

    const contentRect = content.getBoundingClientRect();
    const left = rect.left - contentRect.left + rect.width / 2;
    const top = rect.top - contentRect.top + rect.height / 2;

    canvasContainer.style.left = `${left}px`;
    canvasContainer.style.top = `${top}px`;
    canvasContainer.style.width = `${rect.width}px`;
    canvasContainer.style.height = `${rect.height}px`;
    canvasContainer.style.translate = `-${Math.round(
      rect.width / 2
    )}px -${Math.round(rect.height / 2)}px`;

    fabricContext.canvas.setWidth(rect.width);
    fabricContext.canvas.setHeight(rect.height);
    fabricContext.canvas.setViewportTransform([
      zoomFactor * 10,
      0,
      0,
      zoomFactor * 10,
      0,
      0
    ]);
    return true;
  }, [
    canvasContainerRef,
    contentRef,
    pageRefsMap,
    activePageNumber,
    zoomFactor,
    fabricContext.canvas,
    pageRefsVersion
  ]);

  const runMobileCanvasLayout = useCallback(() => {
    if (!fabricContext.canvas) return;

    if (!isPageSizeReady) {
      setMobileFabricCanvasVisible(false);
      return;
    }

    const synced = syncMobileFabricCanvas();
    if (!synced) {
      setMobileFabricCanvasVisible(false);
      return;
    }

    setMobileFabricCanvasVisible(true);
    fabricContext.canvas.requestRenderAll();
  }, [
    isPageSizeReady,
    fabricContext.canvas,
    syncMobileFabricCanvas,
    setMobileFabricCanvasVisible
  ]);

  const onClearHighlightObjects = useCallback(() => {
    const { canvas } = fabricContext;
    if (!canvas) return;
    const getHighlightObjects = canvas
      .getObjects()
      .filter((item) => item.type === 'highlight');
    getHighlightObjects.forEach((obj) => canvas.remove(obj));
    canvas.fire('object:history:remove');
  }, [fabricContext]);

  const renderTextHighlights = useCallback(
    (rects: any) => {
      if (!fabricContext.canvas) return;
      syncMobileFabricCanvas();
      onClearHighlightObjects();
      highlightCreator({
        canvas: fabricContext.canvas,
        documentHighlightRects: rects,
        viewerActions: {
          selectComment,
          setCanvasMode,
          setDocumentHighlight
        }
      });
    },
    [
      fabricContext.canvas,
      syncMobileFabricCanvas,
      onClearHighlightObjects,
      selectComment,
      setCanvasMode,
      setDocumentHighlight
    ]
  );

  useEffect(() => {
    runMobileCanvasLayout();

    const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
    viewport?.addEventListener('scroll', runMobileCanvasLayout, {
      passive: true
    });
    window.addEventListener('resize', runMobileCanvasLayout);

    return () => {
      viewport?.removeEventListener('scroll', runMobileCanvasLayout);
      window.removeEventListener('resize', runMobileCanvasLayout);
    };
  }, [wrapperRef, runMobileCanvasLayout]);

  useEffect(() => {
    requestAnimationFrame(runMobileCanvasLayout);
  }, [
    selectedCommentId,
    activePageNumber,
    pageRefsVersion,
    mobilePagesReadyVersion,
    zoomFactor,
    runMobileCanvasLayout
  ]);

  return {
    setMobileFabricCanvasVisible,
    syncMobileFabricCanvas,
    runMobileCanvasLayout,
    onClearHighlightObjects,
    renderTextHighlights
  };
}
